import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatStepper, MatStepperModule } from "@angular/material/stepper";
import { TranslocoModule } from "@ngneat/transloco";

export interface WizardStepItem {
  label: string;
  stepControl: AbstractControl;
  hidden?: boolean;
}

export interface WizardButtonOptions {
  label?: string;
  isWide?: boolean;
}

export enum WizardButton {
  cancel = "cancel",
  previous = "previous",
  next = "next",
  complete = "complete"
}

export interface WizardOptions {
  isModalWindow?: boolean;
  modestMobileView?: boolean;
  justifyContentCenter?: boolean;
  hiddenFooterButtons?: boolean;
  button?: {
    [key in WizardButton]?: WizardButtonOptions;
  };
}

const defaultWizardOptions: WizardOptions = {
  isModalWindow: false,
  hiddenFooterButtons: false
};

@Component({
  selector: "lib-wizard",
  templateUrl: "./wizard.component.html",
  styleUrls: ["./wizard.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, MatStepperModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ]
})
export class WizardComponent implements OnInit, OnDestroy {
  @Input() public steps: WizardStepItem[] = [];
  @Input() public templates: TemplateRef<any>[] = [];
  @Input() public options: WizardOptions = defaultWizardOptions;
  @Input() public set fixedContent(value: boolean) {
    this.isContentFixed = value;

    /**
     * When the content is fixed, disable body scrolling to prevent background scrolling
     * while interacting with the wizard.
     */
    if (this.isContentFixed) {
      this.toggleBodyScrolling(false);
    }
  }
  @Input() stretchedContent = false;

  @Output() public stepChange = new EventEmitter<number>();
  @Output() public confirm = new EventEmitter<void>();
  @Output() public cancel = new EventEmitter<void>();
  @Output() public stepperReady = new EventEmitter<WizardComponent>();

  @ViewChild("stepper", { static: true }) stepper!: MatStepper;

  protected isContentFixed = false;
  protected firstVisibleStepIndex = 0;

  static alwaysValidControl = new FormControl<boolean>(true, Validators.requiredTrue);

  get selectedIndex(): number {
    return this.stepper.selectedIndex;
  }

  get selectedLabel(): string {
    return this.stepper.selected?.label || "";
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.updateFirstVisibleStepIndex();
  }

  ngAfterViewInit(): void {
    this.stepperReady.emit(this);
  }

  selectionChange(event: any): void {
    this.stepChange.emit(event.selectedIndex);
  }

  goToStep(step: number): void {
    this.stepper.selectedIndex = step;
  }

  updateView(): void {
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    /**
     * Re-enable body scrolling when the wizard is destroyed
     * if it was previously disabled due to fixed content.
     */
    if (this.isContentFixed) {
      this.toggleBodyScrolling(true);
    }
  }

  /**
   * Toggles the scrollability of the document body
   * @param enableScrolling When true, enables scrolling; when false, disables scrolling
   */
  private toggleBodyScrolling(enableScrolling: boolean): void {
    const noScrollingClass = "not-scrollable";

    if (enableScrolling) {
      document.body.classList.remove(noScrollingClass);
    } else {
      document.body.classList.add(noScrollingClass);
    }
  }

  /**
   * Calculate and update the index of the first visible step
   */
  private updateFirstVisibleStepIndex(): void {
    this.firstVisibleStepIndex = this.steps.findIndex((step) => !step.hidden);
  }
}
